// The chart inputs of a matrix sub-item block, ported from SubItems.Matrix.ChartView
// (renderChartModel + the renderChartModes branches above it).
//
// The chart itself is the shared answer-variants one: a batch draws its own answer variants, the
// same shape a radio-button question draws. What is sub-item specific is that the batch inherits
// its format from the question it hangs on, and that a pie leaves nothing to zoom.

import { getColorTheme, getFeatures, getModelValue, resolveTrait } from '@aytm/app/stats/lib/stat-page'
import { isRType as calcIsRType, isTraitType as calcIsTraitType } from '@aytm/app/stats/page/components/lib/utils'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'
import {
  calcQuestionChartMode,
  calcIgnoreAmount,
  calcIgnorePercents
} from '@aytm/app/stats/page/question-views/components/lib/chart-modes'

const PIE_CHART_MODE = 'pie'

export const calcChartProps = ({
  id,
  questionKey,
  questionType,
  questionSubType,
  format,
  answer,
  answerVariants,
  // a batch may be told to ignore these in its own right, over and above what its format says
  ignoreRefs,
  ignoreAmount,
  ignorePercents,
  degree,
  scaleTmpl,
  scalePow,
  scaleSteps,
  modelChartMode,
  chartMode,
  zoomLevel,
  sortOrder,
  handlers = {}
} = {}) => {
  const traitName = getModelValue('trait')
  const isTraitType = calcIsTraitType({ format, questionType })
  // the report-wide mode is left to the chart, so its selector still reads Default when the reader
  // follows the report
  const resolvedChartMode = calcQuestionChartMode(chartMode, modelChartMode)

  return {
    // the row's own id: the chart wrapper is named after it, apart from the question's own chart
    questionId: id,
    questionKey,
    // The parent's type is deliberately not inherited (the retired sub-item chart view withheld it
    // too): a batch keys its columns by answer variant, while a scale question's own chart keys
    // them by batch and reads each through `scale`, which collapses every point here to zero.
    answer,
    answerVariants,
    sortOrder,
    // the reader's own pick for the batch, else the mode its question declares
    chartMode: resolvedChartMode,
    globalChartMode: getModelValue('chartMode'),
    zoomLevel,
    globalZoomLevel: getModelValue('zoomLevel'),
    // a pie has no axis to zoom, so the handles are withheld rather than left to do nothing
    zoomType: false !== format?.zoomType && PIE_CHART_MODE !== resolvedChartMode,
    ignoreRefs: true === format?.ignoreRefs || Boolean(ignoreRefs),
    ignoreAmount: calcIgnoreAmount(format, ignoreAmount),
    ignorePercents: calcIgnorePercents(format, ignorePercents),
    ignoreTraits: true === format?.ignoreTraits || !isTraitType,
    percentsOnly: true === format?.percentsOnly,
    isRType: calcIsRType({ questionType, questionSubType }),
    // the counting mode itself, so the sub-block's shares, intervals and axis follow it as the question's do
    rType: calcIsRType({ questionType, questionSubType }) ? getModelValue('rType') : 'cases',
    isTraitType,
    traitName,
    trait: resolveTrait(traitName),
    aType: 'A',
    answerType: 'amount',
    degree,
    scaleTmpl,
    scalePow,
    scaleSteps,
    colorTheme: getColorTheme(),
    chartLegendMode: getModelValue('chartLegendMode'),
    confidenceLevel: getModelValue('confidenceLevel'),
    significanceLevel: getModelValue('significanceLevel'),
    confidenceLevels: PageGlobals.CONFIDENCE_LEVELS,
    features: getFeatures(),
    onChartModeChange: handlers.onChartModeChange,
    onZoomChange: handlers.onZoomChange,
    onChartReady: handlers.onChartReady
  }
}
